import { getPickableSubTypes } from "@@/shared/acquire/accommodation-engine/accommodation-resource-helpers";
import type { AccommodationQuantities } from "@@/shared/acquire/accommodation-quantity";
import { isAccommodation } from "@@/shared/acquire/is-accommodation";
import { pickedAccommodationSubTypeFactory } from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/picked-accommodation-sub-type";
import { pickedAccommodationTypeFactory } from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/picked-accommodation-type";
import { isResourceAccommodation } from "@@/shared/acquire/resources/resource-accommodation/resource-accommodation";
import type { ResourceTable } from "@@/shared/acquire/resources/resource-table";
import { isResourceTable } from "@@/shared/acquire/resources/resource-table";
import { isResourceTableGroup } from "@@/shared/acquire/resources/resource-table-group";
import { getPossibilities } from "@@/shared/acquire/sessions/verify-requested-quantities-are-possible-to-distribute-among-resources";
import type { CartId } from "@@/shared/carts/cart-id";
import type { TimeRange } from "@@/shared/date-time/time-range";
import { addMinutesTimeToTimeRange } from "@@/shared/date-time/time-range";
import type { DurationInMinutes } from "@@/shared/date-time/time-spans";
import { zeroPrice } from "@@/shared/prices/price";
import type { Sku } from "@@/shared/products/sku";
import type {
    AvailableQuantity,
    Quantity,
} from "@@/shared/units-and-quantities/quantity";
import { units } from "@@/shared/units-and-quantities/unit";
import { emptyArrayOf } from "@@/utilities/empty";
import { groupBy } from "@@/utilities/group-by";
import { partitionBy } from "@@/utilities/partition-by";
import { repeatIndex } from "@@/utilities/repeat";
import { sortBy } from "@@/utilities/sort";
import { sum } from "@@/utilities/sum";
import {
    getBookableResourceReferences,
    getResourceReferences,
} from "../bookable";
import type {
    AccommodationSettings,
    BookableSettings,
} from "../bookable-settings";
import { isBookable } from "../is-bookable";
import { resourcePriceFixedFactory } from "../resource-price/resource-price-fixed";
import type { BookableOptionValue } from "../resource-reference-options/bookable-option-value";
import { isManifestBookableOptionValue } from "../resource-reference-options/manifest-bookable-option-value";
import type { QuantityPickerBookableOptionValue } from "../resource-reference-options/quantity-picker-bookable-option-value";
import { isQuantityPickerBookableOptionValue } from "../resource-reference-options/quantity-picker-bookable-option-value";
import { getOverrideResourceReference } from "../resource-references/resource-override";
import type { ResourceReference } from "../resource-references/resource-reference";
import type { NonGroupResource, Resource } from "../resources/resource";
import type { ResourceGraph } from "../resources/resource-graph";
import type { ResourceGroup } from "../resources/resource-group";
import { isResourceGroup } from "../resources/resource-group";
import type { ResourceId } from "../resources/resource-id";
import type { ResourceReservationRequestInfo } from "../resources/resource-reservation-info";
import type { ResourceReservationRequest } from "../resources/resource-reservation-request";
import { resourceReservationRequestFactory } from "../resources/resource-reservation-request";
import type { BookableSession } from "./bookable-session";
import { getResourceAvailableQuantity } from "./bookable-session-helper";
import type { BookableSessionId } from "./bookable-session-id";
import { generateAndSyncManifest } from "./booking-engine/generate-and-sync-manifest";
import { findAllGraphs, flattenGraph } from "./flatten-graph";
import { generateResourceTimeRanges } from "./generate-resource-timerange";

type BookingEngineResult = {
    resource: NonGroupResource;
    quantity: number;
    bookableQuantity: number;
};
type AvailableQuantityPerTimeRange = {
    resource: Resource;
    timeRange: TimeRange;
    availableQuantity: AvailableQuantity;
    children?: AvailableQuantityPerTimeRange[]; //For table groups
};

const resourceReservationAccommodationFactory = (params: {
    bookableSku: Sku;
    globalResources: ResourceGraph[];
    timeRange: TimeRange;
    resourceReservationInfo: ResourceReservationRequestInfo[];
    pickedResourceGroups: {
        quantity: number;
        resourceGroupId: ResourceId;
        requestedResources?: ResourceId[];
        displayResourceTitle: boolean;
        quantities: AccommodationQuantities;
    }[];

    settings: AccommodationSettings;
    allowSplitBooking?: boolean;
    cartId?: CartId;
    ignoreTime?: boolean; //Ignore if the reservation is in the past
}): ResourceReservationRequest[] => {
    const {
        bookableSku,
        timeRange,
        settings,
        pickedResourceGroups,
        globalResources,
        resourceReservationInfo,
        //requestedResources,
    } = params;

    // console.log("RES RES ACC FACTORY 1", isAccommodation(bookableSku.acquire));

    if (!isAccommodation(bookableSku.acquire))
        return emptyArrayOf<ResourceReservationRequest>();

    const allResourceGraphs = globalResources.flatMap(r => findAllGraphs(r));
    const resourceReservationRequests = pickedResourceGroups.flatMap(re => {
        const { quantity, resourceGroupId, quantities, displayResourceTitle } =
            re;
        // console.log("RES RES ACC FACTORY 2", re);

        const resourceGraph = allResourceGraphs.find(
            graph => graph.resourceGroup?._id === resourceGroupId,
        );

        if (!resourceGraph) throw new Error("Resource group not found");

        if (isResourceGroup(resourceGraph?.resourceGroup)) {
            const resources = flattenGraph(resourceGraph);

            const accommodationResources = resources.filter(
                isResourceAccommodation,
            );

            //This does not as you would think for accommodation it does not split the booking
            //It only checks if the resource is available /jakob
            // TODO: Should probably be cleaned and refactored to another function
            // TODO: that only returns the available resources then /emanuel
            const [_, resourceAvailability] = calculateQuantityPerResource(
                accommodationResources,
                timeRange,
                settings,
                resourceReservationInfo,
                quantity,
                re.requestedResources,
                params.allowSplitBooking,
                undefined,
                params.ignoreTime ?? false,
            );
            const availableResources = resourceAvailability
                .filter(
                    a =>
                        a.availableQuantity === "INFINITE" ||
                        a.availableQuantity > 0,
                )
                .map(a => a.resource);
            const availableAccommodationResources = availableResources.filter(
                isResourceAccommodation,
            );

            // Make sure the requested accommodation resources
            // are within capacity
            const possibilities = getPossibilities({
                availableResources: availableAccommodationResources,
                requestedQuantities: quantities,
                numberOfResources: quantity,
                preferredResourceCombination: re.requestedResources,
            });

            // console.log("RES RES ACC FACTORY 4", {
            //     availableAccommodationResources,
            //     availableResources,
            //     possibilities,
            //     resources,
            //     re,
            // });

            const reservationRequest =
                possibilities.firstPossibleCombination.reduce(
                    (prev, resourceToBook) => {
                        if (!isResourceAccommodation(resourceToBook))
                            return prev;

                        const subTypesForResource =
                            getPickableSubTypes(resourceToBook);

                        const req = resourceReservationRequestFactory({
                            optionValues: [],
                            resourceId: resourceToBook._id,
                            resourceType: resourceToBook._type,
                            resourceUnit: units.accommodation, //TODO: Fix this or remove resourceUnit
                            timeRange,
                            main: true,
                            quantity: 1, // does this work for accommodations? /emanuel
                            // sessionId: session?._id,
                            pickedBy: re.requestedResources?.some(
                                r => r === resourceToBook._id,
                            )
                                ? "USER"
                                : "SYSTEM",
                            cartId: params.cartId,
                            displayResourceTitle: displayResourceTitle,
                            isLastReservation: true,
                            accommodationType: pickedAccommodationTypeFactory({
                                typeId: resourceToBook.accommodationType._id,
                                title: resourceToBook.accommodationType.title,
                                iconName:
                                    resourceToBook.accommodationType.iconName,
                                shortDescription:
                                    resourceToBook.accommodationType
                                        .shortDescription,

                                ...(subTypesForResource.length === 1
                                    ? {
                                          subType:
                                              pickedAccommodationSubTypeFactory(
                                                  {
                                                      iconName:
                                                          subTypesForResource[0]
                                                              .iconName,
                                                      title: subTypesForResource[0]
                                                          .title,
                                                      typeId: subTypesForResource[0]
                                                          ._id,
                                                      shortDescription:
                                                          subTypesForResource[0]
                                                              .shortDescription,
                                                  },
                                              ),
                                      }
                                    : {}),
                            }),
                        });

                        return [...prev, req];
                    },
                    [] as ResourceReservationRequest[],
                );

            return reservationRequest;
        }
        return [];
    });

    return resourceReservationRequests;
};

const resourceReservationRequestsFactory = (params: {
    bookableSku: Sku;
    globalResources: ResourceGraph[];
    //only used to sync manifest values
    previousRequests: ResourceReservationRequest[];
    session?: BookableSession;
    optionValues: BookableOptionValue[];
    sessionTimeRange: TimeRange;
    resourceReservationInfo: ResourceReservationRequestInfo[];
    requestedResources?: ResourceId[];
    settings: BookableSettings;
    allowSplitBooking?: boolean;
    cartId?: CartId;
    overrideSessionSettings?: boolean;
}): ResourceReservationRequest[] => {
    const {
        bookableSku,
        globalResources,
        session,
        previousRequests,
        optionValues,
        sessionTimeRange,
        resourceReservationInfo,
        requestedResources,
        settings,
        overrideSessionSettings,
    } = params;

    if (isAccommodation(bookableSku.acquire))
        return emptyArrayOf<ResourceReservationRequest>();

    if (!isBookable(bookableSku.acquire))
        return emptyArrayOf<ResourceReservationRequest>();

    /** Antalgrupper */
    const resourceReferences = getBookableResourceReferences(
        bookableSku.acquire,
    );

    /** Antalgrupper (main) */
    const mainResourceReferences = getResourceReferences(
        bookableSku.acquire.main,
    );

    const resourceReservationRequests = resourceReferences
        .map(item => getOverrideResourceReference(session, item))
        .flatMap(resourceReference => {
            const values = optionValues
                .filter(
                    value =>
                        value.resourceReferenceId === resourceReference._id,
                )
                .map(ov => {
                    if (isManifestBookableOptionValue(ov)) return ov;
                    const price =
                        session?.mappings[resourceReference._id]?.data[
                            ov.parentId
                        ]?.resourcePrice;
                    if (!price) return ov;
                    const refOv = {
                        ...ov,
                        resourcePrice: price,
                    } as QuantityPickerBookableOptionValue;

                    return refOv;
                })
                .reduce((prev, current) => {
                    if (isManifestBookableOptionValue(current))
                        return [...prev, current];
                    if (current.quantity.value <= 0) return prev;
                    const existingItem = prev.find(
                        ov => ov._id === current._id,
                    );
                    const prevAmount =
                        existingItem &&
                        !isManifestBookableOptionValue(existingItem)
                            ? existingItem.quantity.value
                            : 0;

                    return [
                        ...prev.filter(ov => ov._id !== current._id),
                        {
                            ...current,

                            quantity: {
                                ...current.quantity,
                                value: current.quantity.value + prevAmount,
                            } as Quantity,
                        } as BookableOptionValue,
                    ];
                }, [] as BookableOptionValue[]);

            const filteredOnlyReserved = values.filter(optionValue => {
                if (isQuantityPickerBookableOptionValue(optionValue))
                    return optionValue.quantity.value > 0;
                return optionValue;
            });
            const manifest = generateAndSyncManifest(
                filteredOnlyReserved,
                resourceReference,
                previousRequests.flatMap(r => r.optionValues), //Old values
            );

            const quantity = sum(
                filteredOnlyReserved.filter(
                    isQuantityPickerBookableOptionValue,
                ),
                ov => ov.quantity.value,
            );

            const resource = resourceReference.resource;
            const resourceTimeRanges = generateResourceTimeRanges(
                sessionTimeRange,
                resourceReference,
                session,
            );

            return resourceTimeRanges.timeRanges.flatMap((timeRange, index) => {
                const isLast = isLastReservation(
                    resourceReference,
                    timeRange,
                    sessionTimeRange,
                );
                if (isResourceGroup(resource)) {
                    const resources = globalResources
                        .filter(
                            graph => graph.resourceGroup?._id === resource._id,
                        )
                        .flatMap(graph => flattenGraph(graph));

                    const [resourcesToBook, _] = calculateQuantityPerResource(
                        resources,
                        timeRange,
                        session?.bookableSettings ?? settings,
                        resourceReservationInfo,
                        quantity,
                        requestedResources,
                        params.allowSplitBooking,
                        session?._id,
                        overrideSessionSettings ?? false,
                    );

                    const reservationRequest = resourcesToBook
                        .sort(
                            sortBy(r =>
                                isResourceTableGroup(r.resource) ? 0 : 1,
                            ),
                        )
                        .reduce((prev, result) => {
                            const values = prev.flatMap(f => f.optionValues);
                            const groupByValues = groupBy(values, f => f._id);
                            const toBookedOptional = filteredOnlyReserved
                                .map(r => {
                                    if (isManifestBookableOptionValue(r))
                                        return r;
                                    const previous = groupByValues.get(r._id);
                                    if (!previous) return r;

                                    const summed = sum(previous, gg =>
                                        isQuantityPickerBookableOptionValue(gg)
                                            ? gg.quantity.value
                                            : 0,
                                    );

                                    return {
                                        ...r,
                                        quantity: {
                                            ...r.quantity,
                                            value: r.quantity.value - summed,
                                        } as Quantity,
                                    } as BookableOptionValue;
                                })
                                .reduce((prev, current) => {
                                    if (isManifestBookableOptionValue(current))
                                        return [...prev, current];
                                    if (current.quantity.value <= 0)
                                        return prev;
                                    const left =
                                        result.quantity -
                                        sum(prev, gg =>
                                            isQuantityPickerBookableOptionValue(
                                                gg,
                                            )
                                                ? gg.quantity.value
                                                : 0,
                                        );

                                    if (left <= 0) return prev;

                                    if (current.quantity.value >= left)
                                        return [
                                            ...prev,
                                            {
                                                ...current,
                                                quantity: {
                                                    ...current.quantity,
                                                    value: left,
                                                } as Quantity,
                                                resourcePrice:
                                                    index === 0
                                                        ? current.resourcePrice
                                                        : resourcePriceFixedFactory(
                                                              {
                                                                  price: zeroPrice,
                                                              },
                                                          ),
                                            } as BookableOptionValue,
                                        ];
                                    return [...prev, current];
                                }, [] as BookableOptionValue[]);

                            const parent = resourcesToBook.find(
                                r =>
                                    isResourceTableGroup(r.resource) &&
                                    r.resource.resources.some(
                                        rr => rr._id === result.resource._id,
                                    ),
                            );

                            const previous = prev.find(
                                r => r.resourceId === parent?.resource._id,
                            );

                            const req = resourceReservationRequestFactory({
                                optionValues: [
                                    ...toBookedOptional,
                                    ...manifest,
                                ],
                                resourceId: result.resource._id,
                                resourceType: result.resource._type,
                                resourceUnit: resource.unit,
                                timeRange,
                                main: mainResourceReferences.some(
                                    f => f._id === resourceReference._id,
                                ),
                                quantity: result.quantity,
                                sessionId: session?._id,
                                pickedBy: requestedResources?.some(
                                    r => r === result.resource._id,
                                )
                                    ? "USER"
                                    : "SYSTEM",
                                cartId: params.cartId,
                                displayResourceTitle:
                                    resourceReference.displayResourceTitle,
                                isLastReservation: isLast,
                                parentReservationId: previous?._id,
                            });
                            return [...prev, req];
                        }, [] as ResourceReservationRequest[]);

                    return reservationRequest;
                } else {
                    // if (isResourceGeneric(resource))
                    //     throw new Error(
                    //         resource._type + " is not supported here",
                    //     );

                    return [
                        resourceReservationRequestFactory({
                            optionValues: [
                                ...filteredOnlyReserved,
                                ...manifest,
                            ],
                            resourceId: resource._id,
                            resourceType: resource._type,
                            // TODO! @Jakob: Gjorde en snabbfix där, den tycker att unit
                            // TODO! inte finns på alla resources vilket är sant
                            resourceUnit:
                                "unit" in resource ? resource.unit : units.pcs,
                            timeRange,
                            main: mainResourceReferences.some(
                                f => f._id === resourceReference._id,
                            ),
                            quantity,
                            sessionId: session?._id,
                            pickedBy: "SYSTEM",
                            cartId: params.cartId,
                            displayResourceTitle:
                                resourceReference.displayResourceTitle,
                            isLastReservation: isLast,
                        }),
                    ];
                }
            });
        });
    return resourceReservationRequests;
};

const isLastReservation = (
    resourceReference: ResourceReference,
    reservationTimeRange: TimeRange,
    sessionTimeRange: TimeRange,
) => {
    if (!resourceReference.schedulingResource) return false;

    return sessionTimeRange.end.unix === reservationTimeRange.end.unix;
};

const calculateQuantityPerResource = (
    resources: Exclude<Resource, ResourceGroup>[],
    timeRange: TimeRange,
    settings: BookableSettings,
    resourceReservationInfo: ResourceReservationRequestInfo[],
    quantity: number,
    requestedResources?: ResourceId[],
    allowSplitBookings?: boolean,
    sessionId?: BookableSessionId,
    overrideSession?: boolean,
) => {
    const adjustedTimeRange = addMinutesTimeToTimeRange({
        timeRange,
        durationBefore: settings.preparationTime ?? (0 as DurationInMinutes),
        durationAfter: settings.postTime,
    });

    const availability = resources
        .map(resource => {
            if (isResourceTableGroup(resource)) {
                //Check if the table group resource is available
                // const reservations = getOverlappingReservations({
                //     reservations: resourceReservationInfo,
                //     resource,
                //     timeRange: adjustedTimeRange,
                // });

                // if (reservations.length > 0) {
                //     return {
                //         resource: resource,
                //         timeRange: adjustedTimeRange,
                //         availableQuantity: 0,
                //         children: [],
                //     };
                // }

                const d = getResourceAvailableQuantity({
                    timeRange: adjustedTimeRange,
                    settings,
                    reservations: resourceReservationInfo,
                    resource: resource,
                    overrideSession: overrideSession ?? false,
                    sessionId,
                });

                if (
                    d.availableQuantity !== "INFINITE" &&
                    d.availableQuantity < quantity
                )
                    return {
                        resource: resource,
                        timeRange: adjustedTimeRange,
                        availableQuantity: 0,
                        children: [],
                    };

                const findTables = resources.filter(r =>
                    resource.resources.some(t => t._id === r._id),
                ) as ResourceTable[];

                const tableAvailability = findTables.map(table => {
                    return getResourceAvailableQuantity({
                        timeRange: adjustedTimeRange,
                        settings,
                        reservations: resourceReservationInfo,
                        resource: table,
                        overrideSession: overrideSession ?? false,
                        sessionId,
                    });
                });

                const allTableInGroupHasAvailableQuantity =
                    tableAvailability.every(
                        r =>
                            r.availableQuantity === "INFINITE" ||
                            r.availableQuantity > 0,
                    );
                if (!allTableInGroupHasAvailableQuantity)
                    return {
                        resource: resource,
                        timeRange: adjustedTimeRange,
                        availableQuantity: 0,
                        children: tableAvailability,
                    };

                // const _summed = sum(tableAvailability, t =>
                //     t.availableQuantity === "INFINITE"
                //         ? 9999
                //         : t.availableQuantity,
                // );

                return {
                    resource: resource,
                    timeRange: adjustedTimeRange,
                    availableQuantity: d.availableQuantity, // summed as AvailableQuantity,
                    children: tableAvailability,
                };
            }

            return getResourceAvailableQuantity({
                timeRange: adjustedTimeRange,
                settings,
                reservations: resourceReservationInfo,
                resource,
                overrideSession: overrideSession ?? false,
                sessionId,
            });
        })
        .sort(sortBy(f => f.availableQuantity, "ASCENDING"))
        .filter(
            r => r.availableQuantity === "INFINITE" || r.availableQuantity > 0,
        )
        .filter(r => {
            if (allowSplitBookings || requestedResources?.length) return true;
            if (isResourceTable(r.resource)) {
                return (
                    r.availableQuantity === "INFINITE" ||
                    r.availableQuantity >= quantity
                );
            }
            return true;
        })
        .filter(r =>
            requestedResources?.length && allowSplitBookings
                ? requestedResources.includes(r.resource._id)
                : true,
        )
        .sort(
            sortBy(
                f => (requestedResources?.includes(f.resource._id) ? 1 : 0),
                "DESCENDING",
            ),
        );

    //If requestedResources is set, we try to evenly distribute the quantity over the resources
    if (allowSplitBookings && requestedResources?.length) {
        const toBookResources = evenlyBookResources(availability, quantity);

        return [toBookResources, availability] as const;
    }
    //If requestedResources is not set, try to maximize the quantity per resource
    const toBookResources = bookResources(
        availability,
        quantity,
        !!requestedResources?.length,
    );

    return [toBookResources, availability] as const;
};

/**
 * try to evenly distribute the quantity over the resources
 *
 * @param {{
 *         resource: Resource;
 *         timeRange: TimeRange;
 *         availableQuantity: AvailableQuantity;
 *     }[]} availability
 * @param {number} quantity
 * @return {*}
 */
const evenlyBookResources = (
    availability: AvailableQuantityPerTimeRange[],
    quantity: number,
) => {
    //Flatten the resources for table groups
    // const flatten = availability.flatMap(r => {
    //     if (r.children) return r.children;
    //     return r;
    // });

    const startValues = availability.reduce((prev, current) => {
        return [
            ...prev,
            {
                resource: current.resource,
                quantity: 0,
                bookableQuantity: current.availableQuantity,
            } as BookingEngineResult,
        ];
    }, [] as BookingEngineResult[]);
    //Place 1 quantity each time, use the emptiest table first
    const toBookResources = repeatIndex(quantity).reduce((prev, _) => {
        const currentResult = prev
            .filter(r => r.bookableQuantity !== r.quantity)
            .sort(r => r.quantity)[0];
        if (!currentResult) return prev;

        const current = availability.find(
            r => r.resource._id === currentResult.resource._id,
        );
        if (!current) return prev;

        const left = quantity - sum(prev, f => f.quantity);

        if (left <= 0) return prev;

        if (
            current.availableQuantity === "INFINITE" ||
            current.availableQuantity > currentResult.quantity
        ) {
            if (current.children) {
                //If the resource is a table group, we need to book all tables in the group
                const children = bookResources(current.children, left, false);
                return [
                    ...prev.filter(
                        r => r.resource._id !== currentResult.resource._id,
                    ),
                    ...children,
                    {
                        resource: current.resource,
                        quantity: left,
                        bookableQuantity: current.availableQuantity,
                    } as BookingEngineResult,
                ];
            }
        }

        if (current.children) {
            const children = bookResources(current.children, left, false);
            return [
                ...prev.filter(
                    r => r.resource._id !== currentResult.resource._id,
                ),
                ...children,
                {
                    resource: current.resource,
                    quantity: left,
                    bookableQuantity: current.availableQuantity,
                } as BookingEngineResult,
            ];
        }

        return [
            ...prev.filter(r => r.resource._id !== currentResult.resource._id),
            {
                resource: currentResult.resource,
                quantity: currentResult.quantity + 1,
                bookableQuantity: current.availableQuantity,
            } as BookingEngineResult,
        ];
    }, startValues);
    return toBookResources.filter(r => r.quantity > 0);
};

const _sortAvailableQuantityPerTimeRange = (
    availability: AvailableQuantityPerTimeRange[],
    quantity: number,
) => {
    const [equalToQuantity, theRest] = partitionBy(
        availability,
        f => f.availableQuantity === quantity,
    );
    return [...equalToQuantity, ...theRest];
};

/**
 *
 * Maximize the quantity per resource
 * Minimize the number of resources needed to be booked
 */
const bookResources = (
    availability: AvailableQuantityPerTimeRange[],
    quantity: number,
    keepOrder: boolean,
): BookingEngineResult[] => {
    //Sort resources with equal quantity first
    //Is mostly used as a fallback-check if the input contains resources with lesser quantity than the requested quantity
    const sortedArr = keepOrder
        ? availability
        : _sortAvailableQuantityPerTimeRange(availability, quantity);

    return sortedArr.reduce((prev, current) => {
        const left = quantity - sum(prev, f => f.quantity);

        if (left <= 0) return prev;

        if (
            current.availableQuantity === "INFINITE" ||
            current.availableQuantity >= left
        ) {
            if (current.children) {
                const children = bookResources(
                    current.children,
                    left,
                    keepOrder,
                );
                return [
                    ...prev,
                    ...children,
                    {
                        resource: current.resource,
                        quantity: left,
                        bookableQuantity: current.availableQuantity,
                    } as BookingEngineResult,
                ];
            }

            return [
                ...prev,
                {
                    resource: current.resource,
                    quantity: left,
                    bookableQuantity: current.availableQuantity,
                } as BookingEngineResult,
            ];
        }
        return [
            ...prev,
            {
                resource: current.resource,
                quantity: current.availableQuantity,
                bookableQuantity: current.availableQuantity,
            } as BookingEngineResult,
        ];
    }, [] as BookingEngineResult[]);
};
export {
    calculateQuantityPerResource,
    resourceReservationAccommodationFactory,
    resourceReservationRequestsFactory,
};
